import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Plus, Search, Play, Clock, Users, BookOpen, Edit, Trash2, Eye, EyeOff, Upload, Image as ImageIcon, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const OnlineCourses = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    imagen_url: '',
    duracion_estimada: 60,
    nivel: 'basico',
    fecha_inicio: '',
    fecha_fin: '',
    is_published: false
  });

  const isAdmin = user?.role === 'Administrador' || user?.role === 'Formador';

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/online-courses`);
      setCourses(response.data);
    } catch (error) {
      toast.error('Error al cargar los cursos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCourse) {
        await axios.put(`${API_URL}/online-courses/${editingCourse.id}`, formData);
        toast.success('Curso actualizado correctamente');
      } else {
        await axios.post(`${API_URL}/online-courses`, formData);
        toast.success('Curso creado correctamente');
      }
      setShowDialog(false);
      resetForm();
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el curso');
    }
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      nombre: course.nombre,
      descripcion: course.descripcion || '',
      imagen_url: course.imagen_url || '',
      duracion_estimada: course.duracion_estimada || 60,
      nivel: course.nivel || 'basico',
      fecha_inicio: course.fecha_inicio || '',
      fecha_fin: course.fecha_fin || '',
      is_published: course.is_published || false
    });
    setShowDialog(true);
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('¿Estás seguro de eliminar este curso? Se eliminarán todos los módulos y lecciones.')) return;
    try {
      await axios.delete(`${API_URL}/online-courses/${courseId}`);
      toast.success('Curso eliminado correctamente');
      fetchCourses();
    } catch (error) {
      toast.error('Error al eliminar el curso');
    }
  };

  const togglePublish = async (course) => {
    try {
      await axios.put(`${API_URL}/online-courses/${course.id}`, {
        ...course,
        is_published: !course.is_published
      });
      toast.success(course.is_published ? 'Curso despublicado' : 'Curso publicado');
      fetchCourses();
    } catch (error) {
      toast.error('Error al cambiar el estado');
    }
  };

  const resetForm = () => {
    setEditingCourse(null);
    setFormData({
      nombre: '',
      descripcion: '',
      imagen_url: '',
      duracion_estimada: 60,
      nivel: 'basico',
      fecha_inicio: '',
      fecha_fin: '',
      is_published: false
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no permitido. Use JPEG, PNG, WebP o GIF');
      return;
    }

    // Validar tamaño (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo es demasiado grande. Máximo 5MB');
      return;
    }

    setUploadingImage(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/upload/image`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, imagen_url: response.data.url });
      toast.success('Imagen subida correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir la imagen');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, imagen_url: '' });
  };

  const filteredCourses = courses.filter(course =>
    course.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (course.descripcion || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getNivelBadge = (nivel) => {
    const colors = {
      basico: 'bg-green-100 text-green-800',
      intermedio: 'bg-yellow-100 text-yellow-800',
      avanzado: 'bg-red-100 text-red-800'
    };
    const labels = {
      basico: 'Básico',
      intermedio: 'Intermedio',
      avanzado: 'Avanzado'
    };
    return <Badge className={colors[nivel] || colors.basico}>{labels[nivel] || nivel}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-manrope font-bold text-3xl text-slate-900">Formación Online</h1>
            <p className="font-inter text-slate-600 mt-1">Cursos de formación a distancia</p>
          </div>
          {isAdmin && (
            <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#004E89] hover:bg-[#003B66]">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Curso
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingCourse ? 'Editar Curso' : 'Nuevo Curso Online'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Nombre del curso *</Label>
                      <Input
                        value={formData.nombre}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Descripción</Label>
                      <Textarea
                        value={formData.descripcion}
                        onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Fecha de inicio *</Label>
                      <Input
                        type="date"
                        value={formData.fecha_inicio}
                        onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Fecha de fin *</Label>
                      <Input
                        type="date"
                        value={formData.fecha_fin}
                        onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Imagen de portada</Label>
                      <div className="mt-2 space-y-3">
                        {/* Preview de imagen actual */}
                        {formData.imagen_url && (
                          <div className="relative w-full h-40 rounded-lg overflow-hidden border border-slate-200">
                            <img
                              src={formData.imagen_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${formData.imagen_url}` : formData.imagen_url}
                              alt="Preview"
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={removeImage}
                              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        
                        {/* Botones de subir/URL */}
                        <div className="flex gap-2">
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageUpload}
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            className="hidden"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingImage}
                            className="flex-1"
                          >
                            {uploadingImage ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600 mr-2"></div>
                                Subiendo...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Subir imagen
                              </>
                            )}
                          </Button>
                        </div>
                        
                        {/* Input URL alternativo */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">o</span>
                          <Input
                            value={formData.imagen_url}
                            onChange={(e) => setFormData({ ...formData, imagen_url: e.target.value })}
                            placeholder="Pegar URL de imagen..."
                            className="flex-1"
                          />
                        </div>
                        <p className="text-xs text-slate-500">Formatos: JPEG, PNG, WebP, GIF. Máximo 5MB</p>
                      </div>
                    </div>
                    <div>
                      <Label>Duración estimada (minutos)</Label>
                      <Input
                        type="number"
                        value={formData.duracion_estimada}
                        onChange={(e) => setFormData({ ...formData, duracion_estimada: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label>Nivel</Label>
                      <Select value={formData.nivel} onValueChange={(v) => setFormData({ ...formData, nivel: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="basico">Básico</SelectItem>
                          <SelectItem value="intermedio">Intermedio</SelectItem>
                          <SelectItem value="avanzado">Avanzado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-[#004E89] hover:bg-[#003B66]">
                      {editingCourse ? 'Guardar cambios' : 'Crear curso'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Buscar cursos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map((course) => (
            <div
              key={course.id}
              className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Image */}
              <div className="h-40 bg-gradient-to-br from-[#004E89] to-[#0EA5E9] relative">
                {course.imagen_url ? (
                  <img 
                    src={course.imagen_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${course.imagen_url}` : course.imagen_url} 
                    alt={course.nombre} 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <BookOpen className="w-16 h-16 text-white/50" />
                  </div>
                )}
                {!course.is_published && isAdmin && (
                  <div className="absolute top-2 right-2">
                    <Badge className="bg-orange-500">Borrador</Badge>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-manrope font-semibold text-lg text-slate-900 line-clamp-2">
                    {course.nombre}
                  </h3>
                  {getNivelBadge(course.nivel)}
                </div>
                <p className="font-inter text-sm text-slate-600 line-clamp-2 mb-4">
                  {course.descripcion || 'Sin descripción'}
                </p>
                <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {Math.floor(course.duracion_estimada / 60)}h {course.duracion_estimada % 60}m
                  </div>
                  {course.categoria && (
                    <div className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      {course.categoria}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    className="flex-1 bg-[#004E89] hover:bg-[#003B66]"
                    onClick={() => navigate(`/online-courses/${course.id}`)}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    {isAdmin ? 'Gestionar' : 'Ver curso'}
                  </Button>
                  {isAdmin && (
                    <>
                      <Button variant="outline" size="icon" onClick={() => togglePublish(course)}>
                        {course.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button variant="outline" size="icon" onClick={() => handleEdit(course)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="icon" className="text-red-600" onClick={() => handleDelete(course.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredCourses.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No hay cursos disponibles</h3>
            <p className="text-slate-500 mt-1">
              {isAdmin ? 'Crea tu primer curso online' : 'Pronto habrá cursos disponibles'}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OnlineCourses;
